import { useEffect, ReactNode } from "react";
import classNames from "classnames";

type Tab = {
  id?: string;
  label: ReactNode;
  path?: string;
  icon?: string;
  badge?: boolean;
};

interface ITabsProps {
  tabs: Tab[];
  onTabChange: (tab: Tab) => void;
  currentTab: string;
  variant?: "default" | "pills" | "segmented";
  size?: "sm" | "md" | "lg";
  tabClassName?: string;
  containerClassName?: string;
}

export const Tabs = ({
  tabs,
  onTabChange,
  currentTab,
  variant = "default",
  size = "md",
  tabClassName,
  containerClassName,
}: ITabsProps) => {



  useEffect(() => {
    // Animation removed
  }, [tabs]);

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-5 py-2.5 text-base gap-2",
  };

  if (variant === "pills") {
    return (
      <div className={classNames("flex flex-col gap-2 rounded-2xl border border-white/20 bg-white/30 p-1.5 shadow-sm backdrop-blur-xl dark:border-content-800/30 dark:bg-content-900/30 lg:flex-row lg:flex-wrap", containerClassName)}>
        {tabs.map((tab) => {
          const tabKey = tab.path || tab.id || tab.label;

          return (
            <button
              type="button"
              role="tab"
              aria-selected={tabKey === currentTab}
              key={tabKey as string}
              onClick={() => onTabChange(tab)}
              className={classNames(
                "tab group relative flex w-full cursor-pointer items-center justify-start overflow-hidden rounded-2xl border font-sans font-medium transition-all duration-200 lg:w-auto lg:justify-center",
                sizeClasses[size],
                tabKey === currentTab
                  ? "bg-primary-600 text-white border-primary-600 shadow-md dark:bg-primary-500 dark:border-primary-500"
                  : "bg-white/40 border-white/20 text-content-600 hover:bg-white/60 hover:text-content-900 dark:bg-content-800/40 dark:border-content-700/30 dark:text-content-400 dark:hover:bg-content-800/60 dark:hover:text-content-200",
                tabClassName
              )}
            >
              {tab.icon && (
                <div
                  className={classNames(
                    tab.icon,
                    "h-4 w-4 relative z-10 transition-colors duration-200",
                    tabKey === currentTab
                      ? "text-white"
                      : "text-content-400 group-hover:text-content-600 dark:text-content-500 dark:group-hover:text-content-300",
                  )}
                />
              )}
              <span className="relative z-10">
                {tab.label}
                {tab.badge && (
                  <span className="absolute -top-1 -right-2 flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-400 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-error-500"></span>
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === "segmented") {
    return (
      <div className="flex w-full overflow-hidden border-b border-content-200 dark:border-content-800">
        <div className="flex w-full min-w-0 flex-col gap-1 px-2 lg:flex-row lg:gap-6">
          {tabs.map((tab) => {
            const tabKey = tab.path || tab.id || tab.label;

            return (
              <div
                aria-selected={tabKey === currentTab}
                key={tabKey}
                onClick={() => onTabChange(tab)}
                className={classNames(
                  "tab group relative -mb-[1px] flex w-full cursor-pointer items-center justify-start px-1 py-3 font-sans transition-all duration-200 lg:w-auto lg:justify-center",
                  tabKey === currentTab
                    ? "text-primary-800 dark:text-primary-100 font-semibold border-b-3 border-b-solid border-primary-800 dark:border-primary-400"
                    : "text-primary-700/60 dark:text-primary-300/60 font-medium border-b-3 border-b-solid border-base-200 hover:text-primary-800 dark:hover:text-primary-200 hover:border-content-300 dark:hover:border-content-600",
                )}
              >
                {tab.icon && (
                  <div
                    className={classNames(
                      tab.icon,
                      "h-4 w-4 relative z-10 transition-colors duration-200 mr-2",
                      tabKey === currentTab
                        ? "text-primary-700 dark:text-primary-300"
                        : "text-primary-600/40 group-hover:text-primary-700/80 dark:text-primary-400/60 dark:group-hover:text-primary-300/80",
                    )}
                  />
                )}
                <span className={classNames("relative z-10 text-sm", tabKey === currentTab ? "opacity-100" : "opacity-60")}>
                  {tab.label}
                  {tab.badge && (
                    <span className="absolute -top-1 -right-2 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-400 opacity-75"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-error-500"></span>
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex overflow-hidden border-b border-primary-200/40 dark:border-primary-800/40">
        <div className="flex min-w-0 flex-1 flex-col lg:flex-row">
          {tabs.map((tab) => {
            const tabKey = tab.path || tab.id || tab.label;

            return (
              <button
                key={tabKey}
                role="tab"
                aria-selected={tabKey === currentTab}
                onClick={() => onTabChange(tab)}
                className={classNames(
                  "tab group relative flex w-full cursor-pointer items-center justify-start overflow-hidden border-b-2 font-sans font-medium transition-all duration-200 lg:w-auto lg:justify-center",
                  sizeClasses[size],
                  tabKey === currentTab
                    ? "border-primary-500 bg-gradient-to-t from-primary-500/10 to-transparent text-primary-700 dark:border-primary-400 dark:from-primary-400/15 dark:text-primary-300"
                    : "border-transparent bg-transparent text-primary-600/60 hover:text-primary-800 hover:bg-primary-50/50 hover:border-primary-300/50 dark:text-primary-400/60 dark:hover:text-primary-200 dark:hover:bg-primary-900/30 dark:hover:border-primary-700/50",
                )}
              >
                {tabKey === currentTab && (
                  <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_100%,rgba(59,130,246,0.12)_0%,transparent_70%)] dark:bg-[radial-gradient(120%_90%_at_50%_100%,rgba(96,165,250,0.15)_0%,transparent_70%)] opacity-100" />
                )}
                {tab.icon && (
                  <div
                    className={classNames(
                      tab.icon,
                      "h-4 w-4 relative z-10 transition-colors duration-200",
                      tabKey === currentTab
                        ? "text-primary-500 dark:text-primary-400"
                        : "text-primary-400/70 group-hover:text-primary-500 dark:text-primary-500/70 dark:group-hover:text-primary-400",
                    )}
                  />
                )}
                <span className="relative z-10">
                  {tab.label}
                  {tab.badge && (
                    <span className="absolute -top-1 -right-2 flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-400 opacity-75"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-error-500"></span>
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile shadow indicators */}
    </div>
  );
};
